import { Injectable } from '@angular/core';
import {
    BehavioralAssessment,
    CustomField
} from '../core-data-model/BehavioralAssessment.model';
import { BaPerson } from '../core-data-model/BehavioralAssessmentPerson.model';
import { BehavioralReportConfiguration } from '../core-data-model/BehavioralReportConfiguration.model';
import { BehavioralReportData } from '../core-data-model/BehavioralReportData.model';
import { CognitiveAssessment } from '../core-data-model/CognitiveAssessment.model';
import { EmailSharingCommand } from '../core-data-model/EmailList.model';
import { Folder } from '../core-data-model/Folder.model';
import { PatternInsights } from '../core-data-model/PatternInsights.interface';
import {
    Person,
    PersonUpdate,
    PersonWithDetails
} from '../core-data-model/Person.model';
import { PersonAssessmentConsent } from '../core-data-model/PersonAssessmentConsent.model';
import { FourOhFourDataService } from '../core-services/404.dataservice';
import { AssessmentsApi } from '../core-services/AssessmentsApi.connect';
import { ContextDataService } from '../core-services/Context.dataservice';
import { JobApi } from '../core-services/JobApi/JobApi.connect';
import { notificationOnError } from '../core-services/Notifications.service';
import { PersonApi, PersonApiUpdate } from '../core-services/PersonApi.connect';
import {
    TableManagerDataService,
    PersonDashboardOverviewExtraJobMatchesTableData
} from '../core-services/TableManager.dataservice';
import { ToolboxApi } from '../core-services/Toolbox.connect';
import { Util } from '../core-utilities/Util';
import { PdfReference } from '../system-services/PdfReference';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

export type PersonDashboard = {
    folder: Folder;
    fullName: string;
    personDetails: PersonWithDetails;
};

// TODO: Decide on a more general name so this can be used in more places or is
// this collection of data really specific to the person dashboard page(s)?
@Injectable({
    providedIn: 'root'
})
export class PersonDashboardService {
    private personId: string;

    personDetails: PersonWithDetails;
    customFields: CustomField[] = [];

    baReportInfo: BehavioralReportData;

    jobMatchesTableData: PersonDashboardOverviewExtraJobMatchesTableData;
    personMatched: string;

    // a temporary hack to get around repeated calls
    myDetailsPromise: {
        promise: Promise<Person>;
        id: string;
    };

    myGetPromise: {
        promise: Promise<PersonDashboard>;
        id: string;
    };

    behavioralShowJobMatch = false;

    constructor(
        private personApi: PersonApi,
        private jobApi: JobApi,
        private assessmentsApi: AssessmentsApi,
        private fourOhFour: FourOhFourDataService,
        private userProfile: UserProfileSettingsService,
        private tableManager: TableManagerDataService,
        public TR: TranslationService,
        public context: ContextDataService,
        public util: Util,
        private toolboxApi: ToolboxApi
    ) {}

    fetchData(id: string, url: string): Promise<PersonDashboard> {
        this.personId = id;
        return this.get(false, url);
    }

    // So I believe that data should be held by these data services. I think this needs work
    // in order to do so since it is a hybrid of holding data and returning data and is therefore
    // confusing. - Will
    async get(
        clearCache = false,
        url: string = null
    ): Promise<PersonDashboard> {
        if (this.myGetPromise && this.myGetPromise.id === this.personId) {
            return this.myGetPromise.promise;
        }
        let dashboard: PersonDashboard = null;
        try {
            dashboard = await this.getPartOne(clearCache);
        } catch (error) {
            if (url) {
                this.fourOhFour.redirectTo404(url);
            }
        }

        this.myGetPromise = {
            promise: this.getPartTwo(dashboard),
            id: this.personId
        };
        return this.myGetPromise.promise;
    }

    private async getPartOne(clearCache = false): Promise<PersonDashboard> {
        let folder: Folder = null;
        const [
            person,
            behavioralAssessments,
            cognitiveAssessments,
            defaultCognitiveTime
        ] = await Promise.all([
            this.getPersonDetails(this.personId),
            this.getAllBehavioralAssessmentsForPerson(
                this.personId,
                clearCache
            ),
            this.getAllCognitiveAssessmentsForPerson(this.personId, clearCache),
            this.hasCognitiveAccess()
                ? this.getPersonDefaultCognitiveTime(this.personId)
                : null
        ]);

        const personDetails = new PersonWithDetails(person, {
            behavioralAssessments,
            cognitiveAssessments,
            defaultCognitiveTime
        });
        folder = personDetails.folder;
        this.context.person = personDetails; // set the context for use by the matches grid
        this.fillInJobMatches();

        return {
            folder,
            personDetails,
            fullName: personDetails.fullName()
        };
    }

    // This method fetches data _after_ personDetails is fetched by getPartOne
    private async getPartTwo(
        personDashboard: PersonDashboard
    ): Promise<PersonDashboard> {
        const personDetails = personDashboard.personDetails;
        const extraPromises: Promise<void>[] = [];
        // Get full job data if they have a job
        if (personDetails.job && personDetails.job.id) {
            extraPromises.push(
                this.jobApi
                    .getJob(personDetails.job.id)
                    .then((job) => {
                        personDetails.job = job;
                    })
                    // 404 means they don't have access to the job
                    .catch(() => {
                        personDetails.hasJobAccess = false;
                    })
            );
        }
        // Get pattern insights if they have a pattern
        if (personDetails.hasBehavioralPattern()) {
            extraPromises.push(
                this.getPatternInsights(personDetails.id).then(
                    (patternInsights) => {
                        personDetails.patternInsights = patternInsights;
                    }
                )
            );
        }
        if (personDetails.originalAssessmentId) {
            extraPromises.push(
                this.assessmentsApi
                    .getBehavioralAssessment(personDetails.originalAssessmentId)
                    .then((assessment) => {
                        this.customFields = assessment.customFields;
                    })
            );
        }
        await Promise.all(extraPromises);
        this.personDetails = personDetails;
        this.updateAssessmentWithScore(personDetails.originalAssessment);
        return personDashboard;
    }

    hasCognitiveAccess(): boolean {
        return this.userProfile.isCognitiveEnabled();
    }

    clearPerson(): void {
        this.myGetPromise = null;
        this.personDetails = null;
    }

    async updatePerson(personDetails: PersonUpdate) {
        const personUpdate: PersonApiUpdate = {
            firstName: personDetails.firstName,
            middleName: personDetails.middleName,
            lastName: personDetails.lastName,
            email: personDetails.email,
            folderPath: personDetails.folder
                ? personDetails.folder.folderPath
                : undefined,
            type: personDetails.type
        };

        if (personDetails.job === 'RemoveJob') {
            personUpdate.removeJobAssociation = true;
        } else if (personDetails.job !== null) {
            personUpdate.associatedJobId = personDetails.job.id;
        }

        await this.update(personUpdate, personDetails.originalObject.id);

        // save the personDetails so the UI shows the updated person correctly
        // retrieving the person again won't always work because the PeopleAndJob search index has a delay
        // This is a temporary solution. We need to move data to DocumentDB so that search shares the same data source as the DB
        // NOTE: Only do this on successful update
        this.personDetails = this.personDetails.clientSideUpdate(personDetails);
        // The job set in personDetails comes from the selected Job in the JobTypeahead.
        // That typeahead makes a peopleandjobs call to get info about the jobs that match the typeahead.
        // The Job object returned by the server does NOT contain the cognitiveWeight data, so the calculated
        // combined jobFit score produced below may be incorrect. This bug requires the addition of that property in the index.
        const jobFit = await this.toolboxApi.getJobFit(
            this.personDetails,
            this.personDetails?.job
        );

        this.personDetails = new PersonWithDetails(this.personDetails, {
            jobFit
        });

        return this.personDetails;
    }

    update(update: PersonApiUpdate, personId = this.personId): Promise<any> {
        return this.personApi.updatePerson(personId, update);
    }

    purge(): Promise<any> {
        return this.personApi.purge(this.personDetails.id);
    }

    private getPersonDetails(personId: string): Promise<Person> {
        if (this.myDetailsPromise && this.myDetailsPromise.id === personId) {
            return this.myDetailsPromise.promise;
        }
        this.myDetailsPromise = {
            promise: new Promise((resolve, reject) =>
                this.personApi
                    .getPersonDetails([personId])
                    .then((people) => {
                        const person = people[0];
                        if (person) {
                            return resolve(person);
                        }
                        reject();
                    })
                    .catch(() => reject())
            ),
            id: personId
        };
        return this.myDetailsPromise.promise;
    }

    private getAllBehavioralAssessmentsForPerson(
        personId: string,
        clearCache: boolean
    ): Promise<BehavioralAssessment[]> {
        return this.assessmentsApi.getAllBehavioralAssessmentsForPerson(
            personId,
            clearCache
        );
    }

    private getAllCognitiveAssessmentsForPerson(
        personId: string,
        clearCache: boolean
    ): Promise<CognitiveAssessment[]> {
        let cognitiveAssessments: Promise<CognitiveAssessment[]> =
            Promise.resolve([]);
        // Checking if user has access to Cognitive APIs before making the call
        if (this.userProfile.isCognitiveEnabled()) {
            cognitiveAssessments =
                this.assessmentsApi.getAllCognitiveAssessmentsForPerson(
                    personId,
                    clearCache
                );
        }
        return cognitiveAssessments;
    }

    private getPersonDefaultCognitiveTime(personId: string): Promise<number> {
        // TODO temporary hack to improve speed
        return this.personApi.getPersonDefaultCognitiveTime(personId);
    }

    @notificationOnError()
    getPatternInsights(
        personId: string,
        languageLocale: string = this.TR.currentLocale
    ): Promise<PatternInsights> {
        return this.assessmentsApi.getPatternInsights(
            personId,
            this.userProfile.currentCompany,
            languageLocale
        );
    }

    @notificationOnError()
    getBaPersonDetails(personId: string): Promise<BaPerson> {
        return this.assessmentsApi.getPersonalDetails(personId);
    }

    getAllBehavioralAssessmentConsentsForPerson(
        personId: string
    ): Promise<PersonAssessmentConsent[]> {
        return this.personApi.getPersonAssessmentConsentDetails(personId);
    }

    fillInJobMatches(): Promise<any> {
        if (this.context.person) {
            if (this.personMatched) {
                // force a refresh. The person page loading is behaving strangely so I can't *just* wipe the table contents tehre
                if (this.personMatched != this.context.person.id) {
                    this.jobMatchesTableData = null;
                }
            }
            if (!this.jobMatchesTableData) {
                this.personMatched = this.context.person.id;
                this.jobMatchesTableData =
                    this.tableManager.makePersonDashboardOverviewExtraJobMatchesTable();
                return this.jobMatchesTableData.search();
            } else {
                return Promise.resolve(this.jobMatchesTableData.contents);
            }
        }
    }

    getBAReportInfo(
        reportConfig: BehavioralReportConfiguration
    ): Promise<BehavioralReportData> {
        return this.personApi
            .getBAReportInfo(
                this.personId,
                this.personDetails.gender,
                reportConfig
            )
            .then((baReport) => {
                this.baReportInfo = baReport;
                return this.baReportInfo;
            });
    }

    getPersonDetailsReport(
        languageLocale = this.TR.currentLocale,
        emailSettings?: EmailSharingCommand
    ): Promise<PdfReference> {
        if (languageLocale !== this.TR.currentLocale) {
            const localPersonDetails: PersonWithDetails = new PersonWithDetails(
                Object.assign({}, this.personDetails)
            );
            return this.getPatternInsights(this.personId, languageLocale)
                .then((patternInsights) => {
                    localPersonDetails.patternInsights = patternInsights;
                    return localPersonDetails;
                })
                .then((translatedPersonDetails) =>
                    this.personApi.getPersonDetailsReport(
                        translatedPersonDetails,
                        this.behavioralShowJobMatch,
                        emailSettings,
                        languageLocale
                    )
                );
        } else {
            return this.personApi.getPersonDetailsReport(
                this.personDetails,
                this.behavioralShowJobMatch,
                emailSettings,
                languageLocale
            );
        }
    }

    getPortablePersonPage(
        languageLocale = this.TR.currentLocale,
        emailSettings?: EmailSharingCommand
    ): Promise<PdfReference> {
        return this.personApi.getPortablePersonPage(
            this.personDetails,
            emailSettings,
            languageLocale
        );
    }

    // Note: PortablePersonPage became PersonSnapshot
    async emailPersonSnapshot(
        emailCommand: EmailSharingCommand,
        reportLocale: string
    ) {
        return this.personApi.emailPersonSnapshot(
            this.personDetails,
            emailCommand,
            reportLocale
        );
    }

    updateAssessmentWithScore(selectedBehavioral: BehavioralAssessment) {
        if (
            selectedBehavioral &&
            selectedBehavioral.pattern &&
            !selectedBehavioral.pattern.scoreId
        ) {
            this.assessmentsApi
                .getBehavioralAssessment(selectedBehavioral.id)
                .then((assessment) => {
                    if (
                        selectedBehavioral.id === assessment.id &&
                        selectedBehavioral.pattern &&
                        assessment.pattern
                    ) {
                        selectedBehavioral.pattern.scoreId =
                            assessment.pattern.scoreId;
                    }
                });
        }
    }
}
